import html2canvas from 'html2canvas';

/**
 * Export Utilities
 * Helper functions for exporting report data and charts
 */

/**
 * Convert data to CSV format
 * @param {Array} data - Array of objects to convert
 * @param {Array} headers - Array of header labels
 * @param {Array} keys - Array of object keys corresponding to headers
 * @returns {string} CSV string
 */
export const convertToCSV = (data, headers, keys) => {
    if (!data || data.length === 0) {
        return '';
    }

    // Create header row
    const headerRow = headers.join(',');

    // Create data rows
    const dataRows = data.map(item => {
        return keys.map(key => {
            let value = item[key];

            // Handle null/undefined
            if (value === null || value === undefined) {
                return '';
            }

            // Escape quotes and wrap in quotes if contains comma
            if (typeof value === 'string') {
                value = value.replace(/"/g, '""');
                if (value.includes(',') || value.includes('\n')) {
                    value = `"${value}"`;
                }
            }

            return value;
        }).join(',');
    });

    return [headerRow, ...dataRows].join('\n');
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV content
 * @param {string} filename - File name
 */
export const downloadCSV = (csvContent, filename = 'report.csv') => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
};

/**
 * Export data to Excel using xlsx library
 * @param {Array} data - Array of objects
 * @param {string} filename - File name
 * @param {string} sheetName - Sheet name
 */
export const exportToExcel = async (data, filename = 'report.xlsx', sheetName = 'Report') => {
    try {
        // Dynamically import xlsx to reduce bundle size
        const XLSX = await import('xlsx');

        // Create worksheet from data
        const worksheet = XLSX.utils.json_to_sheet(data);

        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        // Write file
        XLSX.writeFile(workbook, filename);
    } catch (error) {
        console.error('Error exporting to Excel:', error);
        throw new Error('Failed to export to Excel');
    }
};

/**
 * Export chart to PNG
 * @param {string} chartElementId - ID of the chart container element
 * @param {string} filename - File name
 */
export const exportChartToPNG = async (chartElementId, filename = 'chart.png') => {
    try {
        const chartElement = document.getElementById(chartElementId);

        if (!chartElement) {
            throw new Error('Chart element not found');
        }

        // Capture the chart as canvas
        const canvas = await html2canvas(chartElement, {
            backgroundColor: '#ffffff',
            scale: 2, // Higher quality
            logging: false
        });

        // Convert to blob and download
        canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);
        });
    } catch (error) {
        console.error('Error exporting chart to PNG:', error);
        throw new Error('Failed to export chart');
    }
};

/**
 * Format currency for export
 * @param {number} value - Numeric value
 * @param {string} currency - Currency code
 * @returns {string} Formatted currency string
 */
export const formatCurrencyForExport = (value, currency = 'EUR') => {
    if (value === null || value === undefined) return '';

    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};

/**
 * Format date for export
 * @param {string|Date} date - Date value
 * @returns {string} Formatted date string
 */
export const formatDateForExport = (date) => {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) return '';

    return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).format(dateObj);
};

/**
 * Format number for export
 * @param {number} value - Numeric value
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number string
 */
export const formatNumberForExport = (value, decimals = 0) => {
    if (value === null || value === undefined) return '';

    return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(value);
};

/**
 * Prepare KPI data for export
 * @param {Object} kpis - KPI data object
 * @returns {Array} Array of objects ready for export
 */
export const prepareKPIsForExport = (kpis) => {
    return Object.entries(kpis).map(([key, value]) => ({
        Indicateur: key,
        Valeur: typeof value === 'number' ? formatNumberForExport(value, 2) : value
    }));
};

/**
 * Generate filename with timestamp
 * @param {string} baseName - Base filename
 * @param {string} extension - File extension
 * @returns {string} Filename with timestamp
 */
export const generateFilename = (baseName, extension) => {
    const timestamp = new Date().toISOString().slice(0, 10);
    return `${baseName}_${timestamp}.${extension}`;
};
